import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { persistReducer } from 'redux-persist';
import storage from './storage';

export interface AppSettings {
  theme: 'light' | 'dark';
  language: string;
  timezone: string;
  dateFormat: string;
  timeFormat: '12h' | '24h';
  currency: string;
  notifications: {
    email: boolean;
    push: boolean;
    sms: boolean;
  };
}

export interface AppState {
  isLoading: boolean;
  sidebarOpen: boolean;
  currentRole: 'admin' | 'Manager' | 'Auditor' | 'Department Head' | 'Viewer';
  settings: AppSettings;
  lastActivity: string | null;
  connectionStatus: 'online' | 'offline';
  errors: string[];
  notifications: Array<{
    id: string;
    type: 'info' | 'success' | 'warning' | 'error';
    title: string;
    message: string;
    timestamp: string;
    read: boolean;
  }>;
}

const initialState: AppState = {
  isLoading: false,
  sidebarOpen: true,
  currentRole: 'admin',
  settings: {
    theme: 'light',
    language: 'en',
    timezone: 'UTC',
    dateFormat: 'MM/DD/YYYY',
    timeFormat: '12h',
    currency: 'USD',
    notifications: {
      email: true,
      push: true,
      sms: false,
    },
  },
  lastActivity: null,
  connectionStatus: 'online',
  errors: [],
  notifications: [],
};

const appSlice = createSlice({
  name: 'app',
  initialState,
  reducers: {
    toggleSidebar: state => {
      state.sidebarOpen = !state.sidebarOpen;
    },
    setSidebarOpen: (state, action: PayloadAction<boolean>) => {
      state.sidebarOpen = action.payload;
    },
  },
});

export const { toggleSidebar, setSidebarOpen } = appSlice.actions;

const appPersistConfig = {
  key: 'app',
  storage,
  whitelist: ['sidebarOpen', 'settings', 'lastActivity'],
};

export const appReducer = persistReducer(appPersistConfig, appSlice.reducer);
export default appSlice.reducer;
